/**
 * `?type=expired` on the forgot-password page.
 *
 * Set when a user is bounced here because their password has aged out, so the
 * page can explain why they landed on it instead of just showing the normal
 * "enter your email" copy.
 */

export const EXPIRED_PASSWORD_TYPE = 'expired';

export const PASSWORD_EXPIRED_MESSAGE =
  'Your password has expired, please reset it to continue';

/**
 * Error marker returned by authed tRPC procedures when the caller's credentials
 * have been revoked. Callers that never run frontend code (server-to-server,
 * scripts) read this to tell "reset your password" apart from an ordinary
 * expired login.
 *
 * Lives here so the server that throws it and the client that redirects on it
 * share one definition. This module stays import-free on purpose so both sides
 * can use it.
 *
 * NOTE: `UNAUTHORIZED` alone is not a substitute — authedAdminProcedure and the
 * organization/owner guards all throw UNAUTHORIZED for *permission* failures, so
 * keying a logout on the code would sign out a valid user who merely touched an
 * admin-only procedure.
 */
export const PASSWORD_EXPIRED_ERROR = 'PASSWORD_EXPIRED';

/** Where a revoked user is sent to recover. */
export const PASSWORD_EXPIRED_REDIRECT = `/forgot-password?type=${EXPIRED_PASSWORD_TYPE}`;

/** True when a tRPC error carries the revocation marker. */
export const isPasswordExpiredError = (error: unknown): boolean =>
  (error as { message?: unknown } | null)?.message === PASSWORD_EXPIRED_ERROR;

/**
 * Where getServerSideProps should send a request with no session.
 *
 * getServerAuthSession marks the request when it refuses a revoked session, so a
 * page can tell "logged out" from "credentials revoked" and send the second group
 * somewhere that explains itself instead of a login form they cannot satisfy.
 */
export const signedOutDestination = (req: unknown): string =>
  (req as { sessionRevoked?: boolean } | null)?.sessionRevoked === true
    ? PASSWORD_EXPIRED_REDIRECT
    : '/login';

/**
 * Next puts repeated query params in an array (`?type=a&type=b`), so the raw
 * value is `string | string[] | undefined`. Only an exact single `expired`
 * counts — a repeated param is treated as not set rather than guessed at.
 */
export const isExpiredPasswordRedirect = (type: unknown): boolean =>
  type === EXPIRED_PASSWORD_TYPE;
